<template>
  <div class="fields-group">
    <el-button
      v-if="canDelete"
      icon="el-icon-close"
      class="delete-group"
      type="info"
      circle
      @click.stop="deleteGroup"
    ></el-button>
    <div class="title">
      <span v-show="!editNameEnabled">{{ group.name }}</span>
      <el-input
        v-show="editNameEnabled"
        ref="nameInput"
        v-model="groupName"
        size="mini"
        style="width: 120px"
        autofocus
        @blur="nameInputBlur"
        @keyup.native.enter="nameInputBlur"
      ></el-input>
      <span class="icon-edit" @click="showNameInput">
        <i class="el-icon-edit"></i>
      </span>
    </div>
    <draggable
      class="field-list"
      group="control"
      v-model="group.fields"
      @change="controlsChange"
      :animation="100"
    >
      <div
        v-for="(field, index) in group.fields"
        :key="field._cid || field.code"
        class="field-box"
        :class="{
          'is-required': field.required,
          'is-main': field.main,
          'is-active': field._cid == activeControlCid || field.code == activeControlCid
        }"
        @click="changeControl(field)"
      >
        <div class="field-label">
          <svg-icon v-if="!field.display" icon-class="svgo_hide"></svg-icon>
          {{ field.name }}
          <el-tooltip v-if="field.description" :content="field.description" placement="top">
            <span class="icon-tip pointer">
              <i class="el-icon-info"></i>
            </span>
          </el-tooltip>
        </div>
        <div class="field-value"></div>
        <i class="el-icon-s-flag main-tag"></i>
        <div class="copy-field" type="info" circle @click.stop="handleCopyField(field)">
          <svg-icon icon-class="field_copy-field"></svg-icon>
        </div>
        <div class="delete-field" type="info" circle @click.stop="handleDelField(field, index)">
          <svg-icon icon-class="field_delete"></svg-icon>
        </div>
      </div>
    </draggable>
  </div>
</template>

<script>
import draggable from "vuedraggable"
import { allCanCalcFieldTypes } from "~/data/sheet"
import { getObjectId } from "@/api/sheet/sheet"

export default {
  name: "fields-group",
  components: {
    draggable
  },
  props: {
    group: Object,
    activeControlCid: String,
    allFields: Array,
    canDelete: Boolean
  },
  inject: ["sheet"],
  data() {
    return {
      groupName: "",
      controls: [],
      editNameEnabled: false
    }
  },
  methods: {
    // 切换字段
    changeControl(control) {
      this.$emit("openSetting", { type: "field", control: control })
    },
    // 表格的字段发生变化（这里用于监测表格字段新增事件）
    controlsChange(evt) {
      if (evt.added) {
        this.$emit("openSetting", { type: "field", control: evt.added.element })
      }
    },
    showNameInput(group) {
      this.editNameEnabled = true
      this.groupName = this.group.name
      this.$nextTick(() => this.$refs.nameInput.focus())
    },
    nameInputBlur(group) {
      this.editNameEnabled = false
      this.group.name = this.groupName
    },
    deleteGroup() {
      if (this.group.fields.length) return this.$message.error("分组内还存在字段时不可删除！")
      this.$emit("delete")
    },
    async handleCopyField(field) {
      let copyField = {}
      copyField._cid = await getObjectId()
      copyField = Object.assign({}, field, {
        name: field.name + "_副本",
        _cid: copyField._cid
      })
      this.group.fields.push(copyField)
      this.changeControl(copyField)
    },
    handleDelField(field, index) {
      if (
        allCanCalcFieldTypes.includes(field._type) ||
        (field._type === "calc" && field.calcType !== "NUM")
      ) {
        this.$confirm("删除该字段会清空与它有关联的计算字段的计算表达式，确认删除?", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning"
        })
          .then(() => {
            this.allFields.forEach(item => {
              if (
                item._type == "calc" &&
                (item.expression.includes(field._cid) ||
                  item.expression.includes("${" + item.code + "}"))
              ) {
                item.expression = ""
              }
            })
            this.group.fields.splice(index, 1)
            this.shouldResetSettingTab(field)
          })
          .catch(() => {
            this.$message({
              type: "info",
              message: "已取消删除"
            })
            return
          })
      } else if (field._type === "ref" && field.twoWay) {
        this.$confirm("单向删除，则保留另一侧单向关联；双向删除，则解除两者关联关系", "删除关联", {
          distinguishCancelAndClose: true,
          confirmButtonText: "双向删除",
          cancelButtonText: "单向删除",
          type: "warning"
        })
          .then(() => {
            this.$set(this.sheet.data.editInfo, field._cid, true)
            this.group.fields.splice(index, 1)
            this.shouldResetSettingTab(field)
          })
          .catch(action => {
            if (action === "cancel") {
              this.$set(this.sheet.data.editInfo, field._cid, false)
              this.group.fields.splice(index, 1)
              this.shouldResetSettingTab(field)
            } else {
              this.$message({
                type: "info",
                message: "已取消删除"
              })
              return
            }
          })
      } else {
        this.group.fields.splice(index, 1)
        this.shouldResetSettingTab(field)
      }
    },
    shouldResetSettingTab(field) {
      if (this.activeControlCid == field._cid || this.activeControlCid == field.code)
        this.$emit("openSetting", { type: "reset" })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@/styles/mixins.scss";

.fields-group {
  &:hover {
    border-style: dashed;
    border-color: #ff8000;
    .delete-group {
      visibility: visible;
    }
  }
  .delete-group {
    position: absolute;
    top: -7px;
    right: -5px;
    padding: 1px;
    font-size: 16px;
    background-color: red;
    border-color: red;
    visibility: hidden;
  }
  .title {
    font-weight: bold;
    .icon-edit {
      margin-left: 6px;
      padding: 4px;
      cursor: pointer;
      &:hover {
        color: #50a5ff;
      }
    }
  }
  .field-list {
    height: 100%;
    width: 100%;
    min-height: 100px;
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    justify-content: space-between;
    padding: 12px 8px;
    margin-top: 10px;
    box-sizing: border-box;
    border: 1px solid #dee1e6;
    border-radius: 4px;
    .icon-tip {
      margin-left: 3px;
      &:hover {
        color: #3aacf8;
      }
    }
    ::v-deep {
      .field-box {
        display: flex;
        align-items: center;
        width: calc(50% - 20px);
        padding: 12px;
        margin-bottom: 8px;
        border-radius: 4px;
        box-sizing: border-box;
        height: 50px;
        cursor: grab;
        position: relative;
        &:hover {
          .delete-field,
          .copy-field {
            opacity: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 13px;
            width: 17px;
            height: 17px;
            border-radius: 50%;
            background-color: #c0c4cc;
          }
          .delete-field:hover {
            background-color: #fd6565;
          }
          .copy-field:hover {
            background-color: #1989fa;
          }
        }
        &.is-active {
          background-color: #e3f3ff;
        }
        &.is-required::before {
          content: "*";
          position: absolute;
          left: 4px;
          display: inline-block;
          padding-top: 5px;
          color: red;
        }
        &.is-main .main-tag {
          display: block;
        }
      }
    }
    .field-label {
      @include ellipsis;
      display: inline-block;
      width: 25%;
      min-width: 75px;
      padding-left: 6px;
      height: 24px;
      line-height: 24px;
      margin-right: 8px;
      box-sizing: border-box;
    }
    .field-value {
      display: flex;
      flex: auto;
      height: 30px;
      line-height: 24px;
      box-sizing: border-box;
      padding: 0 8px;
      border: 1px solid #dee1e6;
      border-radius: 4px;
      background-color: #ebedf2;
    }
    .main-tag {
      display: none;
      position: absolute;
      left: 0;
      top: 4px;
      color: lightblue;
    }
    .delete-field,
    .copy-field {
      position: absolute;
      padding: 1px;
      opacity: 0;
      right: -4px;
      top: -4px;
      transition: all 0.2s;
    }
    .copy-field {
      right: 18px;
    }
  }
}
</style>
